import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import {
	type LWApiPublicProjectDetailsParamsRawI,
	type LWApiPublicProjectDetailsResultRawI,
} from '@liquidweb-dev/club-sauce/public/project/index.raw';
import type { ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import { getType } from 'modules/api/asset/listModule';
import api from 'modules/queries/api';
import createDisplayName from 'utility/assetDisplayNames';

export const supplementDetails = (
	data: LWApiPublicProjectDetailsResultRawI,
) => {
	const servers = data.servers?.map((asset) => {
		const assetType = getType(asset.categories, asset.type);
		const displayName = createDisplayName(assetType, asset.type);

		return { ...asset, assetType, displayName };
	});

	const hasFileReplication = servers?.some(
		(server) =>
			server.mesDetails && server.mesDetails.mes_type === 'file_replication',
	);

	const hasDatabaseReplication = servers?.some(
		(server) =>
			server.mesDetails && server.mesDetails.mes_type === 'db_replication',
	);

	const hasMes =
		servers?.some((server) =>
			Boolean(Object.keys(server.mesDetails || {}).length),
		) ||
		data.services?.some((service) =>
			Boolean(Object.keys(service.mesDetails || {}).length),
		);

	const numberOfAssets =
		(data.domains?.length || 0) +
		(data.services?.length || 0) +
		(servers?.length || 0);

	return {
		...data,
		servers,
		numberOfAssets,
		hasFileReplication,
		hasDatabaseReplication,
		hasMes,
	};
};

export const PROJECT_DETAILS_QUERY_KEY = '/project/details';

const useProjectDetails = (
	params: LWApiPublicProjectDetailsParamsRawI,
	options?: Omit<
		UseQueryOptions<LWApiPublicProjectDetailsResultRawI, ApiErrorI>,
		'select'
	>,
) =>
	useQuery({
		queryKey: [PROJECT_DETAILS_QUERY_KEY, params],
		queryFn: () => api.project.apiRaw.details(params),
		select: supplementDetails,
		...options,
	});

export default useProjectDetails;
